import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
    Home,
    Assignment,
    Description,
    Flag,
    Groups,
    School,
    Campaign,
    Subscriptions,
    WorkspacePremium,
    AccountBalance,
    SupportAgent,
    Info,
    AccountCircle,
    TravelExplore,
} from "@mui/icons-material";

import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { ChatBubbleOutlined, ExitToApp, LibraryAdd, LibraryBooksOutlined, MenuBookOutlined, Person } from '@mui/icons-material';
import AuthContext from '../../context/AuthContext';
import { useContext } from 'react';
import { Avatar, Typography, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
const drawerWidth = 280;

const openedMixin = (theme) => ({
    width: drawerWidth,
    backgroundColor: "RGB(41 46 92)",
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    borderRight: 'unset',
    overflowX: 'hidden',
});

const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    backgroundColor: "RGB(41 46 92)",
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        variants: [
            {
                props: ({ open }) => open,
                style: {
                    ...openedMixin(theme),
                    '& .MuiDrawer-paper': openedMixin(theme),
                },
            },
            {
                props: ({ open }) => !open,
                style: {
                    ...closedMixin(theme),
                    '& .MuiDrawer-paper': closedMixin(theme),
                },
            },
        ],
    })
);

const PARENT_PORTAL_ROUTES = {
    "/dashboard": "/parent-portal/dashboard",
    "/assessment": "/parent-portal/assessment",
    "/reports": "/parent-portal/reports",
    "/career-skills": "/parent-portal/career-skills",
    "/council": "/parent-portal/council",
    "/admission-counsellor": "/parent-portal/admission-counsellor",
    "/milestone": "/parent-portal/milestone",
    "/chatbuddy": "/parent-portal/chatbuddy",
    "/subscription": "/parent-portal/subscription",
    "/feedback": "/parent-portal/feedback",
    "/how-it-works": "/parent-portal/how-it-works",
    "/dna": "/parent-portal/dna",
    "/profile": "/parent-portal/profile",
};

export default function MiniDrawer({ open, toggleSidebar }) {
    const theme = useTheme();

    const location = useLocation(); // Hook to get current URL
    const { logout, user } = useContext(AuthContext); // Get logout function

    const menuItems = [
        {
            text: "Dashboard",
            icon: <Home />,
            url: "/dashboard",
            roles: ["Admin", "School", "Student", "Parent"],
        },
        {
            text: "Assessment",
            icon: <Assignment />,
            url: "/assessment",
            roles: ["Student", "Parent"],
        },
        {
            text: "Reports",
            icon: <Description />,
            url: "/reports",
            roles: ["Student", "Parent", "School", "Admin"],

        },
        {
            text: "Career & Skills",
            icon: <School />,
            url: "/career-skills",
            roles: ["Student", "Parent"],

        },
        {
            text: "Career Guide",
            icon: <TravelExplore />,
            url: "/career-guide",
            roles: ["Student", "Parent"],

        },
        {
            text: "Counselling",
            icon: <Groups />,
            url: "/council",
            roles: ["Student", "Parent", "Admin"],

        },
        {
            text: "Admission Counsellor",
            icon: <AccountBalance />,
            url: "/admission-counsellor",
            roles: ["Student", "Parent", "Admin"],

        },

        {
            text: "My Milestone",
            icon: <Flag />,
            url: "/milestone",
            roles: ["Student", "Parent"],

        },
        {
            text: "Chat Buddy",
            icon: <Campaign />,
            url: "/chatbuddy",
            roles: ["Student", "Parent", "School", "Admin"],

        },

        {
            text: "Subscription",
            icon: <WorkspacePremium />,
            url: "/subscription",
            roles: ["Student", "Parent", "School", "Admin"],

        },
        {
            text: "Feedback",
            icon: <Subscriptions />,
            url: "/feedback",
            roles: ["Student", "Parent"],
        },

        {
            text: "Know The Process",
            icon: <Info />,
            url: "/how-it-works",
            roles: ["Student", "Parent"],
        },
        { text: 'Professional DNA', icon: <Groups />, url: '/dna', roles: ['Admin', 'School', 'Student', 'Parent'] },
        // {
        //     text: "Help Desk",
        //     icon: <SupportAgent />,
        //     url: "/",
        //     roles: ["Student", "School"],
        //     disable: true

        // },
        {
            text: "Profile",
            icon: <AccountCircle />,
            url: "/profile",
            roles: ["Student", "Parent"],
        },
        { text: 'Log Out', icon: <ExitToApp />, url: '/' },

    ];

    // Filter menu based on user role
    const filteredMenu = menuItems.filter(item => !item.roles || item.roles.includes(user?.role));
    const getItemUrl = (url) =>
        user?.role === "Parent" ? PARENT_PORTAL_ROUTES[url] || url : url;


    return (
        <Drawer variant="permanent" open={open}>
            <DrawerHeader sx={{ backgroundColor: "RGB(41 46 92)", width: "280px" }}>
                {/* <img
                    src="/assets/images/swayamRiskLogoBlue.png"
                    alt="Company Logo"
                    style={{ backgroundColor: "#F3F3F3", width: isMobile?"150px":"200px" }}

                /> */}
                <IconButton
                    onClick={toggleSidebar}
                    sx={{
                        '&': {
                            position: 'absolute',
                            right: '0px',
                        },
                    }}
                >
                    {open ? (
                        <CloseIcon sx={{ color: '#556773' }} />
                    ) : (
                        <ChevronRightIcon sx={{ color: '#556773' }} />
                    )}
                </IconButton>
            </DrawerHeader>
            {/* <Divider /> */}
            <Box className="logged-in-sidebar">
                {open && <img
                    src="/assets/images/logo-white.png"
                    alt="Company Logo"
                    style={{ width: "150px", height: "28px", marginBottom: "17px" }}

                />}
                {open && <Avatar
                    src={
                        user?.userInfo?.avatar || "/assets/images/defaultAvatar.png"
                    }
                    alt={"Student Name"}
                    sx={{
                        width: "138px",
                        height: "138px",
                        marginBottom: "22px",

                    }}
                />}
                {open && <Typography variant="h6" mb={4} sx={{ fontSize: "18px", fontWeight: "600", color: "#779BCB" }}>
                    {user?.userInfo?.user_name || "User Name"}
                </Typography>}
                <List sx={{ width: "100%" }}>
                    {filteredMenu.map((item) => {
                        const itemUrl = getItemUrl(item.url);
                        const isActive = (itemUrl === "/parent-portal/council" && location.pathname === "/parent-portal/chatbot") ||
                            (itemUrl === "/council" && location.pathname === "/chatbot") ||
                            (itemUrl === "/career-guide" && location.pathname.startsWith("/career-guide")) ||
                            location.pathname === itemUrl;// Check if the current URL matches the item's URL
                        return (
                            <ListItem disabled={item?.disable||false} key={item.text} disablePadding sx={{ display: 'block', width: "100%" }}>
                                <ListItemButton
                                    className={isActive ? 'icon-style-active' : 'icon-style-nonactive'}
                                    component={RouterLink}
                                    disabled={item?.disable||false}
                                    onClick={() => {
                                        if (item.text === "Log Out") {
                                            logout(); // Call logout
                                        }

                                    }}
                                    to={itemUrl}
                                    sx={[
                                        {
                                            minWidth: "100%",
                                            minHeight: 30,
                                            px: 2,
                                            background: isActive ? 'RGB(247 170 71)' : 'inherit', // Active background color

                                        },
                                        open
                                            ? {
                                                justifyContent: 'initial',
                                            }
                                            : {
                                                justifyContent: 'center',
                                            },
                                    ]}
                                >
                                    <ListItemIcon
                                        sx={[
                                            {
                                                fontSize: "13px",
                                                minWidth: 0,
                                                justifyContent: 'center',
                                                color: isActive ? 'RGB (252 227 194)' : 'rgba(119, 155, 203, 1)', // Active icon color

                                            },
                                            open ? { mr: 3 } : { mr: 'auto' },
                                        ]}
                                    >
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={item.text}
                                        sx={[
                                            {
                                                fontSize: '13px',
                                                color: isActive ? 'RGB (252 227 194)' : 'rgba(119, 155, 203, 1)', // Active font color
                                            },
                                            open ? { opacity: 1 } : { opacity: 0 },
                                        ]}
                                    />
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
                </List>
            </Box>
        </Drawer>
    );
}
